import type paper from "paper";
import {
  projectConstructionLines,
  type ConstructionLine,
  type ConstructionOutline,
  type OutlineEndpoint,
  type OutlineSegmentRef,
  type Piece,
  type Project,
  type Vec2,
} from "./Project";

/** Magnetic outlines (#37): a piece/cutout/stitch outline is stored as an
 *  ordered list of references to construction-line sub-segments and re-derived
 *  into concrete geometry on every commit, so editing a source line (or a
 *  segment shared between several parts) updates every dependent outline.
 *
 *  This pass runs in the commit pipeline AFTER the construction-line derives
 *  (offset / mirror / rounding) so an outline traced over a derived line reads
 *  that line's final geometry.
 *
 *  Each leg is built by SAMPLING the source curve between its two endpoints.
 *  That is exact for polyline / polygon construction lines (the common case —
 *  a wallet body and pockets) and a fine-step approximation for curved lines;
 *  exact bézier extraction is a future upgrade. */

/** Arc-length step (mm) for sampling a leg of a curved source line. */
const SAMPLE_STEP_MM = 0.25;
/** Points closer than this (mm) are treated as coincident. */
const EPS_MM = 1e-4;
/** Fraction of the smaller intersecting line's length an `intersection`
 *  endpoint's recomputed crossing may drift from its `hint` before we treat the
 *  original crossing as GONE and refuse to resolve (#901). Snapping to a far
 *  surviving crossing would silently relocate the seam to the wrong part of the
 *  line; returning null instead lets the derive keep the last good geometry. */
const HINT_DRIFT_FRAC = 0.34;
/** Absolute floor (mm) for the drift tolerance, so a very short source line
 *  still tolerates the sampling / rounding jitter of a crossing that stayed put. */
const HINT_DRIFT_FLOOR_MM = 1;

function subpathsOf(cp: paper.CompoundPath): paper.Path[] {
  return cp.children && cp.children.length > 0
    ? (cp.children as paper.Path[])
    : [cp as unknown as paper.Path];
}

/** Parses each referenced construction line into a Paper path exactly once
 *  per derive pass, and disposes them all at the end. */
class LineGeometryCache {
  private compoundCache = new Map<string, paper.CompoundPath | null>();
  constructor(
    readonly lineById: Map<string, ConstructionLine>,
    private scope: paper.PaperScope,
  ) {}

  compound(id: string): paper.CompoundPath | null {
    if (this.compoundCache.has(id)) return this.compoundCache.get(id)!;
    const line = this.lineById.get(id);
    let cp: paper.CompoundPath | null = null;
    if (line) {
      try {
        cp = new this.scope.CompoundPath({
          pathData: line.pathData,
          insert: false,
        });
      } catch {
        cp = null;
      }
    }
    this.compoundCache.set(id, cp);
    return cp;
  }

  subpath(id: string, pathIndex: number): paper.Path | null {
    const cp = this.compound(id);
    if (!cp) return null;
    return subpathsOf(cp)[pathIndex] ?? null;
  }

  dispose() {
    for (const cp of this.compoundCache.values()) cp?.remove();
    this.compoundCache.clear();
  }
}

/** Every construction-line id an outline references — across segment sources
 *  and both endpoints. Used to detect a deleted source (→ auto-bake). */
export function referencedLineIds(outline: ConstructionOutline): Set<string> {
  const ids = new Set<string>();
  for (const seg of outline.segments) {
    ids.add(seg.sourceId);
    for (const ep of [seg.start, seg.end]) {
      if (ep.kind === "intersection") {
        ids.add(ep.aId);
        ids.add(ep.bId);
      } else {
        ids.add(ep.constructionId);
      }
    }
  }
  return ids;
}

function resolveEndpoint(
  ep: OutlineEndpoint,
  cache: LineGeometryCache,
  scope: paper.PaperScope,
): paper.Point | null {
  if (ep.kind === "anchor") {
    const sub = cache.subpath(ep.constructionId, ep.pathIndex);
    const seg = sub?.segments[ep.segmentIndex];
    return seg ? seg.point.clone() : null;
  }
  if (ep.kind === "along") {
    const sub = cache.subpath(ep.constructionId, ep.pathIndex);
    if (!sub) return null;
    const t = Math.min(Math.max(ep.t, 0), 1);
    const loc = sub.getLocationAt(t * sub.length);
    return loc ? loc.point.clone() : null;
  }
  // intersection
  const a = cache.compound(ep.aId);
  const b = cache.compound(ep.bId);
  if (!a || !b) return null;
  const inters = a.getIntersections(b);
  if (inters.length === 0) return null;
  if (!ep.hint) return inters[0]!.point.clone();
  const h = new scope.Point(ep.hint.x, ep.hint.y);
  let best = inters[0]!;
  let bestDist = best.point.getDistance(h);
  for (const it of inters) {
    const d = it.point.getDistance(h);
    if (d < bestDist) {
      bestDist = d;
      best = it;
    }
  }
  // Layer A (#901): if the nearest surviving crossing has drifted far from the
  // hint, the crossing this endpoint was pinned to has DISAPPEARED (e.g. the
  // traced line moved off the edge it met) and `best` is a different, unrelated
  // crossing. Snapping there would relocate the seam to the wrong span of the
  // line — instead refuse to resolve so the derive keeps the last good geometry.
  const tol = Math.max(
    HINT_DRIFT_FLOOR_MM,
    HINT_DRIFT_FRAC * Math.min(a.length, b.length),
  );
  if (bestDist > tol) return null;
  return best.point.clone();
}

/** Where two points sit on `sub`, and the SIGNED arc a leg between them
 *  traverses — the walk {@link sampleLeg} makes. For a closed source the two
 *  endpoints split the loop into two arcs; `reversed` selects the other one.
 *
 *  Split out so the repair pass can ask how FAR a candidate ref would walk
 *  without paying to sample it: a ref that lands on the right two points but
 *  reaches them the long way round has the right endpoints and the wrong leg. */
function legSpan(
  sub: paper.Path,
  startPt: paper.Point,
  endPt: paper.Point,
  reversed: boolean,
): {
  sLoc: paper.CurveLocation;
  eLoc: paper.CurveLocation;
  span: number;
} | null {
  const L = sub.length;
  if (L <= EPS_MM) return null;
  const sLoc = sub.getNearestLocation(startPt);
  const eLoc = sub.getNearestLocation(endPt);
  if (!sLoc || !eLoc) return null;
  const a = sLoc.offset;
  const b = eLoc.offset;
  let span: number;
  if (sub.closed) {
    const fwd = (((b - a) % L) + L) % L;
    span = reversed ? fwd - L : fwd; // reversed → walk the other arc (negative)
  } else {
    span = reversed ? a - b : b - a;
  }
  return { sLoc, eLoc, span };
}

/** Sample the portion of `sub` from `startPt` to `endPt`, guaranteeing the
 *  exact endpoints. Walks span-by-span between the source's vertices so corners
 *  stay crisp (vertices are kept exactly) and straight edges stay minimal (just
 *  their endpoints); only curved spans get intermediate samples. For a closed
 *  source the two endpoints split the loop into two arcs; `reversed` selects the
 *  other one. */
function sampleLeg(
  sub: paper.Path,
  startPt: paper.Point,
  endPt: paper.Point,
  reversed: boolean,
): Vec2[] {
  const L = sub.length;
  const info = legSpan(sub, startPt, endPt, reversed);
  if (!info) return [];
  const { sLoc, eLoc, span } = info;
  const a = sLoc.offset;

  const start: Vec2 = { x: sLoc.point.x, y: sLoc.point.y };
  const end: Vec2 = { x: eLoc.point.x, y: eLoc.point.y };
  const dist = Math.abs(span);
  if (dist <= EPS_MM) return [start];
  const dir = span < 0 ? -1 : 1;

  // Convert a path offset into traversal distance u ∈ [0, dist] from the start.
  const offToU = (o: number): number =>
    sub.closed
      ? dir > 0
        ? (((o - a) % L) + L) % L
        : (((a - o) % L) + L) % L
      : dir > 0
        ? o - a
        : a - o;
  const uToOff = (u: number): number => {
    let off = a + dir * u;
    if (sub.closed) off = ((off % L) + L) % L;
    else off = Math.min(Math.max(off, 0), L);
    return off;
  };
  const pointAt = (u: number): Vec2 | null => {
    const loc = sub.getLocationAt(uToOff(u));
    return loc ? { x: loc.point.x, y: loc.point.y } : null;
  };

  // Boundaries: start, each vertex strictly inside the arc, end.
  const bounds = new Set<number>([0, dist]);
  for (const seg of sub.segments) {
    const o = sub.getOffsetOf(seg.point);
    if (o == null) continue;
    const u = offToU(o);
    if (u > EPS_MM && u < dist - EPS_MM) bounds.add(u);
  }
  const ordered = [...bounds].sort((x, y) => x - y);

  const pts: Vec2[] = [start];
  for (let k = 0; k < ordered.length - 1; k++) {
    const u0 = ordered[k]!;
    const u1 = ordered[k + 1]!;
    // Curved span → add intermediate samples; straight span → endpoints only.
    const mid = sub.getLocationAt(uToOff((u0 + u1) / 2));
    if (mid?.curve && !mid.curve.isStraight()) {
      const steps = Math.max(1, Math.ceil((u1 - u0) / SAMPLE_STEP_MM));
      for (let i = 1; i < steps; i++) {
        const p = pointAt(u0 + ((u1 - u0) * i) / steps);
        if (p) pts.push(p);
      }
    }
    // The vertex at u1 (exact) — except the final boundary, which is `end`.
    if (k < ordered.length - 2) {
      const p = pointAt(u1);
      if (p) pts.push(p);
    }
  }
  pts.push(end);
  return pts;
}

function appendDedup(acc: Vec2[], pts: Vec2[]) {
  for (const p of pts) {
    const last = acc[acc.length - 1];
    if (last && Math.hypot(p.x - last.x, p.y - last.y) < EPS_MM) continue;
    acc.push(p);
  }
}

function fmt(n: number): string {
  return Number.isFinite(n) ? `${Math.round(n * 1000) / 1000}` : "0";
}

function pathFromPoints(pts: Vec2[], closed: boolean): string {
  let p = pts;
  if (closed && p.length > 1) {
    const f = p[0]!;
    const l = p[p.length - 1]!;
    if (Math.hypot(f.x - l.x, f.y - l.y) < EPS_MM) p = p.slice(0, -1);
  }
  if (p.length < 2) return "";
  let d = `M${fmt(p[0]!.x)} ${fmt(p[0]!.y)}`;
  for (let i = 1; i < p.length; i++) d += ` L${fmt(p[i]!.x)} ${fmt(p[i]!.y)}`;
  if (closed) d += " Z";
  return d;
}

/** Sample an outline into its ordered polyline points in the assembled frame,
 *  or null if it can't be built. (Stitch lines consume the points directly;
 *  pieces / cutouts turn them into a path string.) */
function buildOutlinePoints(
  outline: ConstructionOutline,
  cache: LineGeometryCache,
  scope: paper.PaperScope,
): Vec2[] | null {
  const acc: Vec2[] = [];
  for (const seg of outline.segments) {
    const sub = cache.subpath(seg.sourceId, seg.pathIndex);
    if (!sub) return null;
    const startPt = resolveEndpoint(seg.start, cache, scope);
    const endPt = resolveEndpoint(seg.end, cache, scope);
    if (!startPt || !endPt) return null;
    const leg = sampleLeg(sub, startPt, endPt, seg.reversed ?? false);
    if (leg.length === 0) return null;
    appendDedup(acc, leg);
  }
  // A closed outline's last leg ends back on its start, so the start comes out
  // twice. `closed` already joins the ends, so the repeat is a zero-length
  // closing run — and a seam that keeps it can't see the corner it starts on
  // (no pivot hole there, #1130 / #1133). Paths drop it in `pathFromPoints`;
  // stitch lines use these points directly, so drop it here.
  if (outline.closed && acc.length > 2) {
    const f = acc[0]!;
    const l = acc[acc.length - 1]!;
    if (Math.hypot(f.x - l.x, f.y - l.y) < EPS_MM) acc.pop();
  }
  return acc.length >= 2 ? acc : null;
}

/** Build the concrete path string for an outline in the assembled frame, or
 *  null if it can't be built from the current geometry. */
function buildOutlinePath(
  outline: ConstructionOutline,
  cache: LineGeometryCache,
  scope: paper.PaperScope,
): string | null {
  const pts = buildOutlinePoints(outline, cache, scope);
  if (!pts) return null;
  const d = pathFromPoints(pts, outline.closed);
  return d === "" ? null : d;
}

/** Build the concrete path string an outline produces from the given
 *  construction lines, in the assembled frame — or null if it can't be built.
 *  Used by the trace tool for its live preview and the initial baked geometry,
 *  so preview and committed geometry stay identical to the commit-time derive. */
export function buildConstructionOutlinePath(
  outline: ConstructionOutline,
  lines: ConstructionLine[],
  scope: paper.PaperScope,
): string | null {
  const cache = new LineGeometryCache(
    new Map(lines.map((l) => [l.id, l] as const)),
    scope,
  );
  try {
    return buildOutlinePath(outline, cache, scope);
  } finally {
    cache.dispose();
  }
}

/** Sample an outline into its polyline points from the given construction
 *  lines — used to seed a traced stitch line's `points` at creation time. */
export function buildConstructionOutlinePoints(
  outline: ConstructionOutline,
  lines: ConstructionLine[],
  scope: paper.PaperScope,
): Vec2[] | null {
  const cache = new LineGeometryCache(
    new Map(lines.map((l) => [l.id, l] as const)),
    scope,
  );
  try {
    return buildOutlinePoints(outline, cache, scope);
  } finally {
    cache.dispose();
  }
}

/** Build a `ConstructionOutline` that traces the WHOLE of a single construction
 *  line — one anchor→anchor leg per edge (the closing edge included for a closed
 *  line). Used to promote a drawn part / cutout / stitch into a construction-
 *  backed one (#740): the returned outline, fed back through the derive pass,
 *  reproduces the line's geometry (exact for straight edges; curved spans get the
 *  same 0.25 mm sampling as any traced part). Returns null for a compound
 *  (multi-subpath) line or one with fewer than 2 anchors — a single outline has
 *  one ring and one `closed` flag, so it can't represent disjoint loops. */
export function outlineForWholeLine(
  line: ConstructionLine,
  scope: paper.PaperScope,
): ConstructionOutline | null {
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return null;
  }
  try {
    const subs = subpathsOf(cp);
    if (subs.length !== 1) return null;
    const sub = subs[0]!;
    const n = sub.segments.length;
    if (n < 2) return null;
    const closed = sub.closed;
    const anchor = (k: number): OutlineEndpoint => ({
      kind: "anchor",
      constructionId: line.id,
      pathIndex: 0,
      segmentIndex: k,
    });
    const segments: OutlineSegmentRef[] = [];
    const lastEdge = closed ? n - 1 : n - 2;
    for (let i = 0; i <= lastEdge; i++) {
      segments.push({
        sourceId: line.id,
        pathIndex: 0,
        start: anchor(i),
        end: anchor((i + 1) % n),
      });
    }
    return { segments, closed };
  } finally {
    cp.remove();
  }
}

/** Drop EVERY construction↔host link in the project, keeping each host's last
 *  geometry (pieces, cutouts, stitch lines). Runs in place of
 *  `deriveConstructionOutlines` when the `construction-connections` paid feature
 *  is OFF, so no construction-to-shape relation is ever kept for free users
 *  (#740). The construction lines themselves are untouched — only the links are
 *  dropped. No-op (returns the input) when nothing is linked. */
export function bakeAllConstructionOutlines(project: Project): Project {
  const linked = project.pieces.some(
    (p) =>
      p.constructionOutline ||
      p.cutouts.some((c) => c.constructionOutline) ||
      (p.stitchLines ?? []).some((s) => s.constructionOutline),
  );
  if (!linked) return project;
  return {
    ...project,
    pieces: project.pieces.map((piece) => {
      let next = bakeConstructionOutline(piece);
      const cutouts = next.cutouts.map((cut) => {
        if (!cut.constructionOutline) return cut;
        const { constructionOutline: _drop, ...rest } = cut;
        return rest;
      });
      if (cutouts.some((c, i) => c !== next.cutouts[i])) {
        next = { ...next, cutouts };
      }
      const stitches = next.stitchLines ?? [];
      const nextStitches = stitches.map((line) => {
        if (!line.constructionOutline) return line;
        const { constructionOutline: _drop, ...rest } = line;
        return rest;
      });
      if (nextStitches.some((s, i) => s !== stitches[i])) {
        next = { ...next, stitchLines: nextStitches };
      }
      return next;
    }),
  };
}

/** Tolerance (mm) for treating an intersection crossing as sitting exactly on a
 *  source-line vertex in {@link anchorSingleLineStitchOutline}. */
const VERTEX_TOL_MM = 1e-2;

/** Layer B (#901): a stitch seam traced along a SINGLE construction line should
 *  anchor its endpoints to THAT line, not to a crossing with a bystander line.
 *  When every segment traces the same source line and an `intersection` endpoint
 *  sits exactly on one of that line's OWN vertices, the crossing is really just
 *  the traced line's own endpoint recorded as an intersection with whatever line
 *  happened to pass through it. Rewriting it to an `anchor` on that vertex makes
 *  the endpoint ride the traced line when it moves — so a LEAD tracing the whole
 *  line and a clipped FOLLOWER on a sub-part stay in sync — instead of
 *  re-deriving to the (stationary) crossing and desyncing / relocating.
 *
 *  Left untouched: a multi-line trace (a genuine corner junction of two DIFFERENT
 *  construction lines has more than one source line, so its intersection
 *  endpoints legitimately track the junction), and a crossing that is NOT at a
 *  source vertex (a mid-line junction with a bystander line — still an
 *  intersection, so Layer A guards it from relocating). */
export function anchorSingleLineStitchOutline(
  outline: ConstructionOutline,
  lines: ConstructionLine[],
  scope: paper.PaperScope,
): ConstructionOutline {
  if (outline.segments.length === 0) return outline;
  const sourceId = outline.segments[0]!.sourceId;
  if (!outline.segments.every((s) => s.sourceId === sourceId)) return outline;
  // Every endpoint must lie on the single source line, else it isn't a pure
  // single-line trace (a corner junction with another line stays intersection).
  const onSource = (ep: OutlineEndpoint): boolean =>
    ep.kind === "intersection"
      ? ep.aId === sourceId || ep.bId === sourceId
      : ep.constructionId === sourceId;
  for (const s of outline.segments) {
    if (!onSource(s.start) || !onSource(s.end)) return outline;
  }
  const cache = new LineGeometryCache(
    new Map(lines.map((l) => [l.id, l] as const)),
    scope,
  );
  try {
    let changed = false;
    const anchorIfVertex = (
      ep: OutlineEndpoint,
      pathIndex: number,
    ): OutlineEndpoint => {
      if (ep.kind !== "intersection") return ep;
      const sub = cache.subpath(sourceId, pathIndex);
      if (!sub) return ep;
      const pt = resolveEndpoint(ep, cache, scope);
      if (!pt) return ep;
      let bestIdx = -1;
      let bestDist = Infinity;
      sub.segments.forEach((seg, i) => {
        const d = seg.point.getDistance(pt);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      });
      if (bestIdx < 0 || bestDist > VERTEX_TOL_MM) return ep;
      changed = true;
      return {
        kind: "anchor",
        constructionId: sourceId,
        pathIndex,
        segmentIndex: bestIdx,
      };
    };
    const segments = outline.segments.map((s) => {
      const start = anchorIfVertex(s.start, s.pathIndex);
      const end = anchorIfVertex(s.end, s.pathIndex);
      return start === s.start && end === s.end ? s : { ...s, start, end };
    });
    return changed ? { ...outline, segments } : outline;
  } finally {
    cache.dispose();
  }
}

/** Clip an OPEN traced-stitch outline to the span(s) on `partPathData`,
 *  returning ONE sub-outline per contiguous on-part run (#199). When one trace
 *  fans a seam out to several overlapping parts, each part should carry only the
 *  portion of the construction line on its own leather — so a bigger part gets
 *  more holes than a smaller one, instead of every part cloning the full-length
 *  seam. Narrowing the outline's `t` range (not just `points`) is what makes the
 *  clip survive re-derivation.
 *
 *  The whole outline (all segments end-to-end) is sampled ~1mm and split into
 *  the runs where it's on the part (inside, or within `EDGE_TOL` of the edge).
 *  Each run becomes its own outline — so a seam that leaves and re-enters the
 *  part (a notch, a slot) yields SEPARATE seams with a real gap, not one row
 *  bridging the hole. A run that spans a construction-line junction stays a
 *  single outline (the junction endpoint is kept), and a trimmed end becomes an
 *  `along` endpoint while an end already on the part keeps its original
 *  (anchor / intersection) endpoint so the magnetic link survives.
 *
 *  A **closed** loop only PARTLY on the part (e.g. a rectangular seam wider than
 *  the part it crosses) is cut into the open arcs that lie on it — wrap-aware, so
 *  an on-part run spanning the loop's start/origin stays one arc; a loop entirely
 *  on the part is kept whole and closed. Returns `[]` when nothing lands on the
 *  part. Clip ends are accurate to the ~1mm sample step. */
export function clipStitchOutlineToPart(
  outline: ConstructionOutline,
  lines: ConstructionLine[],
  partPathData: string,
  scope: paper.PaperScope,
): ConstructionOutline[] {
  const cache = new LineGeometryCache(
    new Map(lines.map((l) => [l.id, l] as const)),
    scope,
  );
  let part: paper.PathItem | null = null;
  try {
    part = new scope.CompoundPath({ pathData: partPathData, insert: false });
  } catch {
    cache.dispose();
    return [outline];
  }
  const TOL = 0.5;
  const onPart = (pt: paper.Point) =>
    part!.contains(pt) ||
    (part!.getNearestLocation(pt)?.point.getDistance(pt) ?? Infinity) <= TOL;

  // Per-segment arc geometry (null = unresolvable → that segment is dropped from
  // sampling). `span` is the SIGNED arc the leg traverses from start to end; on a
  // closed source it's the forward wrap (or `reversed` → the other way round), so
  // a leg ending at the source's origin doesn't read as a full backward sweep.
  const seg = outline.segments;
  const info: ({
    aArc: number;
    bArc: number;
    span: number;
    L: number;
    closed: boolean;
  } | null)[] = seg.map((s) => {
    const sub = cache.subpath(s.sourceId, s.pathIndex);
    if (!sub || !(sub.length > 0)) return null;
    const aPt = resolveEndpoint(s.start, cache, scope);
    const bPt = resolveEndpoint(s.end, cache, scope);
    if (!aPt || !bPt) return null;
    const L = sub.length;
    const aArc = sub.getNearestLocation(aPt)?.offset ?? 0;
    const bArc = sub.getNearestLocation(bPt)?.offset ?? 0;
    let span: number;
    if (sub.closed) {
      const fwd = (((bArc - aArc) % L) + L) % L;
      span = s.reversed ? fwd - L : fwd;
    } else {
      span = s.reversed ? aArc - bArc : bArc - aArc;
    }
    return { aArc, bArc, span, L, closed: sub.closed };
  });

  // Sample the whole outline end-to-end, tagging each sample with its segment +
  // arc (wrapped into the source's `[0, L]`) so a run can be mapped back to
  // per-segment `t` ranges.
  type Sample = { si: number; arc: number; on: boolean };
  const samples: Sample[] = [];
  for (let si = 0; si < seg.length; si++) {
    const inf = info[si];
    if (!inf || Math.abs(inf.span) < EPS_MM) continue;
    const sub = cache.subpath(seg[si]!.sourceId, seg[si]!.pathIndex)!;
    const n = Math.max(8, Math.min(400, Math.round(Math.abs(inf.span))));
    for (let i = 0; i <= n; i++) {
      const raw = inf.aArc + (inf.span * i) / n;
      const arc = inf.closed
        ? ((raw % inf.L) + inf.L) % inf.L
        : Math.max(0, Math.min(inf.L, raw));
      const loc = sub.getLocationAt(arc);
      samples.push({ si, arc, on: !!(loc && onPart(loc.point)) });
    }
  }

  // A closed loop entirely on the part stays one whole (closed) ring.
  if (outline.closed && samples.length > 0 && samples.every((s) => s.on)) {
    part.remove();
    cache.dispose();
    return [outline];
  }

  // Contiguous on-part runs (each a list of samples in loop order).
  const runs: Sample[][] = [];
  let cur: Sample[] | null = null;
  for (const s of samples) {
    if (!s.on) {
      cur = null;
      continue;
    }
    if (!cur) {
      cur = [];
      runs.push(cur);
    }
    cur.push(s);
  }
  // Closed loop: an on-part run straddling the loop's start/origin shows up as a
  // run at the tail AND the head — stitch them into one arc across the seam.
  if (
    outline.closed &&
    runs.length > 1 &&
    samples[0]!.on &&
    samples[samples.length - 1]!.on
  ) {
    const head = runs.shift()!;
    runs[runs.length - 1] = [...runs[runs.length - 1]!, ...head];
  }

  const alongAt = (si: number, arc: number): OutlineEndpoint => ({
    kind: "along",
    constructionId: seg[si]!.sourceId,
    pathIndex: seg[si]!.pathIndex,
    t: Math.min(Math.max(arc / info[si]!.L, 0), 1),
  });

  const out: ConstructionOutline[] = [];
  for (const run of runs) {
    // Group consecutive samples by segment; each group is one clipped segment
    // from its first to last on-part arc (handles a run that wraps the loop's
    // segment list — the groups stay in run order).
    const groups: { si: number; from: number; to: number }[] = [];
    for (const s of run) {
      const g = groups[groups.length - 1];
      if (g && g.si === s.si) g.to = s.arc;
      else groups.push({ si: s.si, from: s.arc, to: s.arc });
    }
    const segs: OutlineSegmentRef[] = [];
    for (const g of groups) {
      const inf = info[g.si];
      if (!inf || Math.abs(g.to - g.from) < EPS_MM) continue;
      const orig = seg[g.si]!;
      segs.push({
        ...orig,
        start:
          Math.abs(g.from - inf.aArc) < EPS_MM
            ? orig.start
            : alongAt(g.si, g.from),
        end:
          Math.abs(g.to - inf.bArc) < EPS_MM ? orig.end : alongAt(g.si, g.to),
      });
    }
    // A clipped run is an OPEN arc even when cut from a closed loop.
    if (segs.length > 0)
      out.push({ ...outline, closed: false, segments: segs });
  }
  part.remove();
  cache.dispose();
  return out;
}

/** The construction-line ids a piece's outline references (sources + endpoint
 *  lines). Empty when the piece isn't traced. Used to surface "trace"
 *  connections between a part and its construction lines. */
export function tracedLineIds(piece: Piece): string[] {
  if (!piece.constructionOutline) return [];
  return [...referencedLineIds(piece.constructionOutline)];
}

/**
 * Every construction-line id ANYTHING on this part traces (#965) — the
 * outline's, plus each cut-out's and each seam's.
 *
 * `tracedLineIds` deliberately answers only for the OUTLINE, because that is
 * what a trace CONNECTION is between: the part and the lines its shape follows.
 * "The lines behind this part" is a different question, and the part-local
 * visibility toggle asks that one: a part whose shape was drawn by hand can
 * still have every cut-out traced, and hiding the outline's sources alone would
 * leave that scaffolding on screen.
 *
 * Order is stable (outline first, then cut-outs, then seams, each in model
 * order) with duplicates removed, so a caller can show the first as a label
 * without the name shuffling between renders.
 */
export function partSourceLineIds(piece: Piece): string[] {
  const out: string[] = [];
  const add = (entity: { constructionOutline?: ConstructionOutline }) => {
    if (!entity.constructionOutline) return;
    for (const id of referencedLineIds(entity.constructionOutline)) {
      if (!out.includes(id)) out.push(id);
    }
  };
  add(piece);
  for (const cut of piece.cutouts ?? []) add(cut);
  for (const seam of piece.stitchLines ?? []) add(seam);
  return out;
}

/** Drop the outline link, keeping the entity's last-derived geometry. Used to
 *  "detach" (bake) a linked part into an ordinary independent one, and when a
 *  source line is deleted. */
export function bakeConstructionOutline(piece: Piece): Piece {
  if (!piece.constructionOutline) return piece;
  const { constructionOutline: _drop, ...rest } = piece;
  return rest;
}

/** Points closer than this (mm) to a source line count as still lying on it.
 *  A traced seam's points come off the source itself, so a survivor sits on it
 *  to within sampling noise. Half a mm is slack for that noise — an offset that
 *  was re-emitted through the trim's sampler has moved a fraction of a mm from
 *  where the seam traced it — while staying far below the width of any real
 *  gap, which is a whole edge. */
const ON_SOURCE_TOL_MM = 0.5;
/** Step (mm) for walking a seam span to ask whether all of it is still on a
 *  source. Fine enough that a removed edge cannot hide between samples,
 *  coarse enough to keep the pass cheap on a long seam. */
const SPAN_SAMPLE_MM = 1;

/** Cut a seam's points down to the runs that still lie on `sourcePaths`, and
 *  return null when there is nothing to change (everything survived, or so
 *  little did that trimming would destroy the seam rather than shorten it).
 *
 *  Tests SEGMENTS, not points. A seam traced round a rectangle is five points
 *  — the corners — and deleting one edge leaves every one of those corners
 *  exactly where it was, so asking "is this point still on the line" answers
 *  yes for all of them and trims nothing. What went missing is the span
 *  BETWEEN two of them, so each span is sampled along its length and kept only
 *  if all of it is still there.
 *
 *  A closed seam's points are a ring, so a surviving run that spans the point
 *  list's start arrives as a head and a tail and has to be rejoined — else
 *  removing one edge of a traced rectangle would break the seam at its start
 *  as well as at the gap. Only the longest run is kept: a `StitchLine` is one
 *  polyline, so a cut leaving two disjoint stretches cannot be expressed as
 *  one seam. */
function trimSeamToLines(
  points: Vec2[],
  closed: boolean,
  sourcePaths: string[],
  scope: paper.PaperScope,
): { points: Vec2[]; closed: boolean } | null {
  if (points.length < 3 || sourcePaths.length === 0) return null;
  const parsed: paper.CompoundPath[] = [];
  for (const d of sourcePaths) {
    try {
      parsed.push(new scope.CompoundPath({ pathData: d, insert: false }));
    } catch {
      /* skip an unparseable source */
    }
  }
  if (parsed.length === 0) return null;
  const onSource = (x: number, y: number): boolean => {
    const pt = new scope.Point(x, y);
    for (const cp of parsed) {
      for (const sub of subpathsOf(cp)) {
        const near = sub.getNearestPoint(pt);
        if (near && pt.getDistance(near) <= ON_SOURCE_TOL_MM) return true;
      }
    }
    return false;
  };
  /** A span survives when every sample along it is still on a source. */
  const spanSurvives = (a: Vec2, b: Vec2): boolean => {
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const steps = Math.max(3, Math.ceil(len / SPAN_SAMPLE_MM));
    for (let k = 0; k <= steps; k++) {
      const t = k / steps;
      if (!onSource(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t)) return false;
    }
    return true;
  };
  const spanCount = closed ? points.length : points.length - 1;
  const alive: boolean[] = [];
  for (let i = 0; i < spanCount; i++) {
    alive.push(spanSurvives(points[i]!, points[(i + 1) % points.length]!));
  }
  for (const cp of parsed) cp.remove();
  if (alive.every(Boolean)) return null;
  if (!alive.some(Boolean)) return null;
  // Each surviving run of spans becomes a run of points: span i covers
  // points i → i+1, so a run of spans [i..j] is points [i..j+1].
  const runs: Vec2[][] = [];
  let current: Vec2[] | null = null;
  for (let i = 0; i < spanCount; i++) {
    if (alive[i]) {
      if (!current) {
        current = [points[i]!];
        runs.push(current);
      }
      current.push(points[(i + 1) % points.length]!);
    } else {
      current = null;
    }
  }
  if (closed && runs.length > 1 && alive[0] && alive[spanCount - 1]) {
    const head = runs.shift()!;
    // The tail already ends on the point the head starts from.
    runs[runs.length - 1]!.push(...head.slice(1));
  }
  let best: Vec2[] | null = null;
  for (const run of runs) if (!best || run.length > best.length) best = run;
  if (!best) return null;
  // A closed seam carries its start again as its last point, so the run that
  // wraps the ring picks that repeat up. Drop coincident neighbours rather
  // than leave a zero-length span in an open seam.
  const out: Vec2[] = [];
  for (const p of best) {
    const last = out[out.length - 1];
    if (last && Math.hypot(p.x - last.x, p.y - last.y) < EPS_MM) continue;
    out.push(p);
  }
  if (out.length < 2) return null;
  return { points: out, closed: false };
}

/** What kind of entity an outline belongs to. A seam is a single polyline and
 *  can lose a stretch and still be a seam; a part / cut-out outline is a closed
 *  region and cannot. */
type OutlineHost = "piece" | "cutout" | "seam";

/** What to do with one traced entity: leave it alone, give it a rewritten
 *  outline, or drop the link and keep its last geometry. */
type OutlineDecision =
  | { kind: "keep" }
  | { kind: "relink"; outline: ConstructionOutline }
  | { kind: "bake" };

/** Walk every piece / cutout / seam whose outline references any of `idSet` and
 *  apply `decide` to it. The two passes over a destructive edit — bake and
 *  repair — differ only in what they decide, so the traversal (and the seam
 *  trimming that a bake does on the way out) lives here once. */
function mapTracedEntities(
  project: Project,
  idSet: Set<string>,
  /** Current geometry of the edited lines, for trimming a baked seam. */
  sources: string[],
  scope: paper.PaperScope | undefined,
  decide: (outline: ConstructionOutline, host: OutlineHost) => OutlineDecision,
): Project {
  let changed = false;
  const traces = (outline: ConstructionOutline | undefined): boolean => {
    if (!outline) return false;
    for (const id of referencedLineIds(outline)) if (idSet.has(id)) return true;
    return false;
  };
  const pieces = project.pieces.map((piece) => {
    let next = piece;
    if (next.constructionOutline && traces(next.constructionOutline)) {
      const d = decide(next.constructionOutline, "piece");
      if (d.kind === "bake") {
        next = bakeConstructionOutline(next);
        changed = true;
      } else if (d.kind === "relink") {
        next = { ...next, constructionOutline: d.outline };
        changed = true;
      }
    }
    const cutouts = next.cutouts.map((cut) => {
      if (!cut.constructionOutline || !traces(cut.constructionOutline)) {
        return cut;
      }
      const d = decide(cut.constructionOutline, "cutout");
      if (d.kind === "keep") return cut;
      changed = true;
      if (d.kind === "relink")
        return { ...cut, constructionOutline: d.outline };
      const { constructionOutline: _drop, ...rest } = cut;
      return rest;
    });
    if (cutouts.some((c, i) => c !== next.cutouts[i])) {
      next = { ...next, cutouts };
    }
    const stitchLines = (next.stitchLines ?? []).map((seam) => {
      if (!seam.constructionOutline || !traces(seam.constructionOutline)) {
        return seam;
      }
      const d = decide(seam.constructionOutline, "seam");
      if (d.kind === "keep") return seam;
      changed = true;
      if (d.kind === "relink") {
        return { ...seam, constructionOutline: d.outline };
      }
      const { constructionOutline: _drop, ...rest } = seam;
      // A BAKED seam loses the stretch that is no longer there to follow. Its
      // points lie ON the lines it traced, so "still traced" is just "still
      // within a hair of one of them" — no index bookkeeping involved, which
      // is the whole reason this works where re-anchoring the refs doesn't.
      const trimmed = scope
        ? trimSeamToLines(rest.points, !!rest.closed, sources, scope)
        : null;
      if (!trimmed) return rest;
      return { ...rest, points: trimmed.points, closed: trimmed.closed };
    });
    if (stitchLines.some((s, i) => s !== (next.stitchLines ?? [])[i])) {
      next = { ...next, stitchLines };
    }
    return next;
  });
  return changed ? { ...project, pieces } : project;
}

/** The geometry the edited lines have NOW — called after the edit is applied,
 *  so these are the shapes that survived it. */
function survivingSources(project: Project, idSet: Set<string>): string[] {
  return projectConstructionLines(project)
    .filter((l) => idSet.has(l.id))
    .map((l) => l.pathData)
    .filter((d) => !!d);
}

/** Bake every piece / cutout / seam that traces any of `lineIds`, keeping the
 *  geometry each one currently shows.
 *
 *  An outline addresses its source by ANCHOR INDEX, so it only stays meaningful
 *  while the source keeps the anchors that were traced. Every DESTRUCTIVE edit
 *  rebuilds a line's path around what survives — deleting a segment, an anchor
 *  or a handle, pruning a stub, slicing, a freehand cut, trimming an offset
 *  (#116) — which renumbers, and in places removes, those anchors. The stored
 *  indices then resolve to points scattered along the new path, and the derive
 *  dutifully joins them: the traced seam runs out along one stretch and comes
 *  straight back along another. (Reproducible on a bare construction rectangle
 *  with a filled seam and no offset in the project at all, so it long predates
 *  #116 — that issue just added one more way in.)
 *
 *  Dropping the link instead leaves what the user drew exactly where it is and
 *  stops it following a line that no longer has the segments it was tracing.
 *  Same treatment `deriveConstructionOutlines` already gives an outline whose
 *  source line was deleted, for the same reason: what it referenced is gone.
 *
 *  This is now the FALLBACK: {@link repairOutlinesTracingLine} rewrites the refs
 *  and keeps the link where it can, and bakes only what it can't repair.
 *
 *  Returns the input project unchanged when nothing traces the lines. */
export function bakeOutlinesTracingLine(
  project: Project,
  lineIds: string | ReadonlyArray<string>,
  /** Paper scope, so a seam can be trimmed to what survived. Omit to keep
   *  every entity's geometry whole (the caller has no scope to hand). */
  scope?: paper.PaperScope,
): Project {
  const ids = typeof lineIds === "string" ? [lineIds] : lineIds;
  if (ids.length === 0) return project;
  const idSet = new Set(ids);
  return mapTracedEntities(
    project,
    idSet,
    survivingSources(project, idSet),
    scope,
    () => ({ kind: "bake" }),
  );
}

/** How far (mm) a rebuilt leg may sit from the leg it replaces and still count
 *  as the same leg. A surviving leg comes back exactly where it was; the slack
 *  is for a source that passed through a sampler on the way — a trimmed offset
 *  is re-emitted a fraction of a mm from where the seam traced it. It stays far
 *  below the size of any real change, which is a whole edge. Doubles as the
 *  tolerance for "this endpoint still lands where it did". */
const LEG_MATCH_TOL_MM = 0.5;
/** Relative slack on the arc a rebuilt leg walks, on top of
 *  {@link LEG_MATCH_TOL_MM}. Scales with leg length so a re-sampled long curve
 *  isn't rejected over accumulated rounding. */
const LEG_ARC_TOL_FRAC = 0.02;
/** Cap on how many points along a leg are compared. A curved leg samples every
 *  0.25 mm, which is far more resolution than "is this the same leg" needs. */
const LEG_MATCH_SAMPLES = 48;

function polylineFrom(pts: Vec2[], scope: paper.PaperScope): paper.Path | null {
  if (pts.length < 2) return null;
  const path = new scope.Path({ insert: false });
  for (const p of pts) path.add(new scope.Point(p.x, p.y));
  return path;
}

/** Worst distance from any of `pts` to `path`, sampling at most
 *  {@link LEG_MATCH_SAMPLES} of them (both ends always included). */
function maxGapToPath(
  pts: Vec2[],
  path: paper.Path,
  scope: paper.PaperScope,
): number {
  const stride = Math.max(1, Math.ceil(pts.length / LEG_MATCH_SAMPLES));
  let worst = 0;
  for (let i = 0; i < pts.length; i += stride) {
    const pt = new scope.Point(pts[i]!.x, pts[i]!.y);
    const near = path.getNearestPoint(pt);
    worst = Math.max(worst, near ? pt.getDistance(near) : Infinity);
  }
  const last = pts[pts.length - 1]!;
  const lastPt = new scope.Point(last.x, last.y);
  const nearLast = path.getNearestPoint(lastPt);
  return Math.max(worst, nearLast ? lastPt.getDistance(nearLast) : Infinity);
}

/** Do two polylines cover the same ground? Measured BOTH ways, because one
 *  direction alone would accept a leg that covers only part of the other. */
function polylinesMatch(
  a: Vec2[],
  b: Vec2[],
  scope: paper.PaperScope,
): boolean {
  const pa = polylineFrom(a, scope);
  const pb = polylineFrom(b, scope);
  try {
    if (!pa || !pb) return false;
    return (
      maxGapToPath(a, pb, scope) <= LEG_MATCH_TOL_MM &&
      maxGapToPath(b, pa, scope) <= LEG_MATCH_TOL_MM
    );
  } finally {
    pa?.remove();
    pb?.remove();
  }
}

/** Re-point one endpoint at `on`, given where it resolved BEFORE the edit.
 *
 *  Keeps the original ref whenever it still lands in the same place — that is
 *  the strongest link available (an `intersection` follows a junction; an
 *  `anchor` whose index came through the renumbering unscathed follows its
 *  vertex). Failing that it re-anchors to whichever vertex is now there, and
 *  falls back to `along` when the point is mid-edge, which is what a re-emitted
 *  offset leaves behind. */
function repairEndpoint(
  ep: OutlineEndpoint,
  at: paper.Point,
  on: { id: string; pathIndex: number },
  nextCache: LineGeometryCache,
  scope: paper.PaperScope,
): OutlineEndpoint {
  const still = resolveEndpoint(ep, nextCache, scope);
  if (still && still.getDistance(at) <= LEG_MATCH_TOL_MM) return ep;
  const sub = nextCache.subpath(on.id, on.pathIndex);
  if (!sub) return ep;
  let bestIdx = -1;
  let bestDist = Infinity;
  sub.segments.forEach((s, i) => {
    const d = s.point.getDistance(at);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  });
  if (bestIdx >= 0 && bestDist <= LEG_MATCH_TOL_MM) {
    return {
      kind: "anchor",
      constructionId: on.id,
      pathIndex: on.pathIndex,
      segmentIndex: bestIdx,
    };
  }
  const loc = sub.getNearestLocation(at);
  if (loc && sub.length > EPS_MM) {
    return {
      kind: "along",
      constructionId: on.id,
      pathIndex: on.pathIndex,
      t: Math.min(Math.max(loc.offset / sub.length, 0), 1),
    };
  }
  return ep;
}

/** One leg's fate: still drawn somewhere (with the ref that finds it now), gone
 *  with the edit, or unreadable — in which case the whole outline falls back to
 *  the bake, since a leg we can't even resolve is a leg we can't reason about.
 *  `arc` is how long the leg was, used to pick the biggest surviving run. */
type LegRepair =
  | { kind: "kept"; ref: OutlineSegmentRef; arc: number }
  | { kind: "gone"; arc: number }
  | { kind: "unknown" };

/** Find where one leg went. Rebuilds the leg from the PRE-edit geometry, then
 *  looks for a ref into the POST-edit geometry that reproduces it.
 *
 *  Both halves of the test matter. Covering the same ground rules out a ref
 *  that walks somewhere else; matching the ARC ruled out the subtle one — an
 *  open path whose two ends are the deleted edge's endpoints has refs that
 *  resolve to exactly the right two points and reach them by going all the way
 *  round the other way, which samples down to a straight line across the gap.
 *  That is the doubling-back defect wearing a different hat. */
function repairLeg(
  seg: OutlineSegmentRef,
  prevCache: LineGeometryCache,
  nextCache: LineGeometryCache,
  candidates: ReadonlyArray<{ id: string; pathIndex: number }>,
  scope: paper.PaperScope,
): LegRepair {
  const prevSub = prevCache.subpath(seg.sourceId, seg.pathIndex);
  if (!prevSub) return { kind: "unknown" };
  const startPt = resolveEndpoint(seg.start, prevCache, scope);
  const endPt = resolveEndpoint(seg.end, prevCache, scope);
  if (!startPt || !endPt) return { kind: "unknown" };
  const wasReversed = seg.reversed ?? false;
  const before = legSpan(prevSub, startPt, endPt, wasReversed);
  if (!before) return { kind: "unknown" };
  const target = sampleLeg(prevSub, startPt, endPt, wasReversed);
  if (target.length < 2) return { kind: "unknown" };
  const arc = Math.abs(before.span);
  const arcTol = Math.max(LEG_MATCH_TOL_MM, LEG_ARC_TOL_FRAC * arc);

  // The leg's own ref first: an edit elsewhere on the line leaves it reading
  // exactly what it read before, so the common case rewrites nothing.
  const ordered = [
    { id: seg.sourceId, pathIndex: seg.pathIndex },
    ...candidates.filter(
      (c) => !(c.id === seg.sourceId && c.pathIndex === seg.pathIndex),
    ),
  ];
  for (const cand of ordered) {
    const sub = nextCache.subpath(cand.id, cand.pathIndex);
    if (!sub || !(sub.length > EPS_MM)) continue;
    const a = sub.getNearestPoint(startPt);
    const b = sub.getNearestPoint(endPt);
    if (!a || !b) continue;
    if (a.getDistance(startPt) > LEG_MATCH_TOL_MM) continue;
    if (b.getDistance(endPt) > LEG_MATCH_TOL_MM) continue;
    for (const reversed of [wasReversed, !wasReversed]) {
      const after = legSpan(sub, a, b, reversed);
      if (!after) continue;
      if (Math.abs(Math.abs(after.span) - arc) > arcTol) continue;
      const built = sampleLeg(sub, a, b, reversed);
      if (built.length < 2) continue;
      if (!polylinesMatch(target, built, scope)) continue;
      const ref: OutlineSegmentRef = {
        sourceId: cand.id,
        pathIndex: cand.pathIndex,
        start: repairEndpoint(seg.start, startPt, cand, nextCache, scope),
        end: repairEndpoint(seg.end, endPt, cand, nextCache, scope),
      };
      if (reversed) ref.reversed = true;
      return { kind: "kept", ref, arc };
    }
  }
  return { kind: "gone", arc };
}

function sameEndpoint(a: OutlineEndpoint, b: OutlineEndpoint): boolean {
  if (a.kind === "anchor" && b.kind === "anchor") {
    return (
      a.constructionId === b.constructionId &&
      a.pathIndex === b.pathIndex &&
      a.segmentIndex === b.segmentIndex
    );
  }
  if (a.kind === "along" && b.kind === "along") {
    return (
      a.constructionId === b.constructionId &&
      a.pathIndex === b.pathIndex &&
      Math.abs(a.t - b.t) < 1e-9
    );
  }
  if (a.kind === "intersection" && b.kind === "intersection") {
    return a.aId === b.aId && a.bId === b.bId;
  }
  return false;
}

function sameOutline(a: ConstructionOutline, b: ConstructionOutline): boolean {
  if (a.closed !== b.closed) return false;
  if (a.segments.length !== b.segments.length) return false;
  return a.segments.every((s, i) => {
    const t = b.segments[i]!;
    return (
      s.sourceId === t.sourceId &&
      s.pathIndex === t.pathIndex &&
      (s.reversed ?? false) === (t.reversed ?? false) &&
      sameEndpoint(s.start, t.start) &&
      sameEndpoint(s.end, t.end)
    );
  });
}

/** Rewrite an outline so it still describes what it drew, or null when it
 *  can't be — which sends the caller to the bake. */
function repairOutline(
  outline: ConstructionOutline,
  host: OutlineHost,
  prevCache: LineGeometryCache,
  nextCache: LineGeometryCache,
  candidates: ReadonlyArray<{ id: string; pathIndex: number }>,
  scope: paper.PaperScope,
): ConstructionOutline | null {
  if (outline.segments.length === 0) return null;
  const legs = outline.segments.map((s) =>
    repairLeg(s, prevCache, nextCache, candidates, scope),
  );
  if (legs.some((l) => l.kind === "unknown")) return null;
  if (legs.every((l) => l.kind === "kept")) {
    return {
      ...outline,
      segments: legs.map((l) => (l as { ref: OutlineSegmentRef }).ref),
    };
  }
  // Something the outline traced is genuinely gone. A part or cut-out outline
  // is a closed region: drop an edge and what's left isn't one, so those bake
  // (geometry whole, link dropped) exactly as before. A seam is a polyline and
  // simply gets shorter.
  if (host !== "seam") return null;
  const n = legs.length;
  const alive = legs.map((l) => l.kind === "kept");
  if (!alive.some(Boolean)) return null;
  const runs: number[][] = [];
  let current: number[] | null = null;
  for (let i = 0; i < n; i++) {
    if (alive[i]) {
      if (!current) {
        current = [];
        runs.push(current);
      }
      current.push(i);
    } else {
      current = null;
    }
  }
  // A closed outline's legs are a ring, so a surviving run that spans the
  // list's start arrives as a head and a tail and has to be rejoined — else
  // dropping one edge of a traced rectangle would break the seam at its start
  // as well as at the gap.
  if (outline.closed && runs.length > 1 && alive[0] && alive[n - 1]) {
    const head = runs.shift()!;
    runs[runs.length - 1]!.push(...head);
  }
  // Only the longest run survives: a StitchLine is ONE polyline, so a cut
  // leaving two disjoint stretches can't be expressed as one seam.
  let best: number[] = [];
  let bestArc = -1;
  for (const run of runs) {
    const len = run.reduce((sum, i) => {
      const leg = legs[i]!;
      return sum + (leg.kind === "unknown" ? 0 : leg.arc);
    }, 0);
    if (len > bestArc) {
      bestArc = len;
      best = run;
    }
  }
  if (best.length === 0) return null;
  return {
    ...outline,
    closed: false,
    segments: best.map((i) => (legs[i] as { ref: OutlineSegmentRef }).ref),
  };
}

/** Keep every piece / cutout / seam tracing `lineIds` MAGNETIC across a
 *  destructive edit, by rewriting its refs to point at what survived instead of
 *  dropping the link (#1029).
 *
 *  `prev` is the project as it was before the edit and `next` the project with
 *  it applied — the pairing is what makes this tractable. A destructive edit
 *  never MOVES geometry, it only removes some, so a leg that is still drawn is
 *  still drawn in exactly the same place: each leg is rebuilt from `prev`, then
 *  matched against `next` to find the ref that reproduces it now. Legs whose
 *  edge went are dropped, which is not the same as "legs whose anchors went" —
 *  delete the top edge of a traced rectangle and both of that leg's endpoints
 *  survive as the two ends of the now-open path, so a naive index remap keeps
 *  the leg and walks it the long way round.
 *
 *  Where a repair isn't possible the entity falls back to
 *  {@link bakeOutlinesTracingLine}: geometry kept, seam trimmed, link dropped.
 *
 *  Returns `next` unchanged when nothing traces the lines. */
export function repairOutlinesTracingLine(
  prev: Project,
  next: Project,
  lineIds: string | ReadonlyArray<string>,
  scope: paper.PaperScope,
): Project {
  const ids = typeof lineIds === "string" ? [lineIds] : lineIds;
  if (ids.length === 0) return next;
  const idSet = new Set(ids);
  const prevLines = projectConstructionLines(prev);
  const nextLines = projectConstructionLines(next);
  const prevIds = new Set(prevLines.map((l) => l.id));
  // Where a leg may have ended up: the lines the edit rewrote, plus anything it
  // created — a slice hands half a line's legs to a brand-new id.
  const candidateIds = [
    ...new Set([
      ...ids,
      ...nextLines.filter((l) => !prevIds.has(l.id)).map((l) => l.id),
    ]),
  ];
  const prevCache = new LineGeometryCache(
    new Map(prevLines.map((l) => [l.id, l] as const)),
    scope,
  );
  const nextCache = new LineGeometryCache(
    new Map(nextLines.map((l) => [l.id, l] as const)),
    scope,
  );
  try {
    const candidates: { id: string; pathIndex: number }[] = [];
    for (const id of candidateIds) {
      const cp = nextCache.compound(id);
      if (!cp) continue;
      subpathsOf(cp).forEach((_, pathIndex) =>
        candidates.push({ id, pathIndex }),
      );
    }
    return mapTracedEntities(
      next,
      idSet,
      survivingSources(next, idSet),
      scope,
      (outline, host) => {
        const repaired = repairOutline(
          outline,
          host,
          prevCache,
          nextCache,
          candidates,
          scope,
        );
        if (!repaired) return { kind: "bake" };
        return sameOutline(outline, repaired)
          ? { kind: "keep" }
          : { kind: "relink", outline: repaired };
      },
    );
  } finally {
    prevCache.dispose();
    nextCache.dispose();
  }
}

function sourcesMissing(
  outline: ConstructionOutline,
  lineById: Map<string, ConstructionLine>,
): boolean {
  for (const id of referencedLineIds(outline)) {
    if (!lineById.has(id)) return true;
  }
  return false;
}

function samePoints(a: Vec2[], b: Vec2[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      Math.abs(a[i]!.x - b[i]!.x) > EPS_MM ||
      Math.abs(a[i]!.y - b[i]!.y) > EPS_MM
    ) {
      return false;
    }
  }
  return true;
}

/** Commit-pipeline pass: re-derive the geometry of every piece, cutout, and
 *  stitch line that carries a `constructionOutline`, from the current
 *  construction lines. Returns the input project unchanged when nothing needs
 *  updating (no-op contract shared with the other derive passes). A linked
 *  entity whose source line(s) have been deleted is auto-baked (link dropped,
 *  last geometry kept). */
export function deriveConstructionOutlines(
  project: Project,
  scope: paper.PaperScope,
): Project {
  const linked = project.pieces.some(
    (p) =>
      p.constructionOutline ||
      p.cutouts.some((c) => c.constructionOutline) ||
      (p.stitchLines ?? []).some((s) => s.constructionOutline),
  );
  if (!linked) return project;

  const lineById = new Map(
    projectConstructionLines(project).map((l) => [l.id, l] as const),
  );
  const cache = new LineGeometryCache(lineById, scope);
  let projectChanged = false;
  try {
    const nextPieces = project.pieces.map((piece) => {
      let next = piece;
      let pieceChanged = false;

      // Piece outline.
      if (next.constructionOutline) {
        if (sourcesMissing(next.constructionOutline, lineById)) {
          next = bakeConstructionOutline(next);
          pieceChanged = true;
        } else {
          const d = buildOutlinePath(next.constructionOutline, cache, scope);
          if (d != null && d !== next.shapePathData) {
            next = { ...next, shapePathData: d };
            pieceChanged = true;
          }
        }
      }

      // Cutouts: derive each cutout's pathData.
      const nextCutouts = next.cutouts.map((cut) => {
        const outline = cut.constructionOutline;
        if (!outline) return cut;
        if (sourcesMissing(outline, lineById)) {
          const { constructionOutline: _drop, ...rest } = cut;
          return rest;
        }
        const d = buildOutlinePath(outline, cache, scope);
        return d == null || d === cut.pathData ? cut : { ...cut, pathData: d };
      });
      if (nextCutouts.some((c, i) => c !== next.cutouts[i])) {
        next = { ...next, cutouts: nextCutouts };
        pieceChanged = true;
      }

      // Stitch lines: derive each seam's points (consumed directly, no path).
      const stitches = next.stitchLines ?? [];
      const nextStitches = stitches.map((line) => {
        const outline = line.constructionOutline;
        if (!outline) return line;
        if (sourcesMissing(outline, lineById)) {
          const { constructionOutline: _drop, ...rest } = line;
          return rest;
        }
        const pts = buildOutlinePoints(outline, cache, scope);
        if (
          !pts ||
          (samePoints(pts, line.points) &&
            (line.closed ?? false) === outline.closed)
        ) {
          return line;
        }
        return { ...line, points: pts, closed: outline.closed };
      });
      if (nextStitches.some((s, i) => s !== stitches[i])) {
        next = { ...next, stitchLines: nextStitches };
        pieceChanged = true;
      }

      if (pieceChanged) projectChanged = true;
      return next;
    });
    return projectChanged ? { ...project, pieces: nextPieces } : project;
  } finally {
    cache.dispose();
  }
}
